import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "@/lib/router-compat";
import { trackCall, trackQuote } from "@/lib/track";
import {
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Phone,
  Clock,
  ShieldCheck,
  Thermometer,
  Ruler,
  ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { getScreedSystem, screedSystems } from "@/data/screedSystems";
import { ContactAndMap } from "@/components/ContactAndMap";
import { ScreedFinder } from "@/components/ScreedFinder";

const ServiceDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const system = slug ? getScreedSystem(slug) : undefined;

  useEffect(() => {
    if (system) {
      document.title = system.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", system.metaDescription);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", system.metaTitle);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", system.metaDescription);
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl)
        ogUrl.setAttribute(
          "content",
          `https://exeterflowscreed.co.uk/services/${system.slug}`,
        );
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical)
        canonical.setAttribute(
          "href",
          `https://exeterflowscreed.co.uk/services/${system.slug}`,
        );
      const twTitle = document.querySelector('meta[name="twitter:title"]');
      if (twTitle) twTitle.setAttribute("content", system.metaTitle);
      const twDesc = document.querySelector('meta[name="twitter:description"]');
      if (twDesc) twDesc.setAttribute("content", system.metaDescription);
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage)
        ogImage.setAttribute(
          "content",
          "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6a85f76b5113a1715f109275.webp",
        );
    }
    window.scrollTo(0, 0);
  }, [system]);

  if (!system) {
    return <Navigate to="/" replace />;
  }

  const Icon = system.icon;
  const otherSystems = screedSystems.filter((s) => s.slug !== system.slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: system.name,
    description: system.metaDescription,
    provider: {
      "@type": "LocalBusiness",
      name: "Exeter Flow Screeds",
      telephone: "07765 712609, 07470 861869",
      areaServed: "Exeter, Devon, South West England",
    },
    serviceType: "Liquid Screed Installation",
    areaServed: "Exeter, Devon, South West England",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: system.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://exeterflowscreed.co.uk/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://exeterflowscreed.co.uk/#what-we-do",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: system.name,
        item: `https://exeterflowscreed.co.uk/services/${system.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-stone-900 font-sans">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main>
        {/* Breadcrumb (hidden visually, kept in DOM for SEO) */}
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sr-only"
          aria-hidden="true"
        >
          <nav className="flex items-center gap-1.5 text-xs text-stone-500">
            <Link to="/" className="hover:text-[#B38D30] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link
              to="/#what-we-do"
              className="hover:text-[#B38D30] transition-colors"
            >
              Services
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-stone-700 font-medium">{system.name}</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 pb-16 sm:pb-20 overflow-hidden bg-stone-950">
          <div className="absolute inset-0 bg-gradient-to-br from-[#141311] via-stone-900 to-black" />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 80% 20%, #C9A84C 0%, transparent 50%)",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#EAD18B] text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6">
                {system.category}
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 rounded-2xl bg-[#C9A84C]/15 text-[#C9A84C]">
                  <Icon className="w-10 h-10" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
                    {system.name} in Exeter & Devon
                  </h1>
                  <p className="text-base sm:text-lg text-[#C9A84C] font-semibold mt-1">
                    {system.tagline}
                  </p>
                </div>
              </div>
              <p className="text-lg sm:text-xl text-stone-300 leading-relaxed mb-8">
                {system.description} We supply and install this across Exeter,
                Devon and the South West.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  onClick={() => trackQuote("service_hero")}
                  className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold text-base px-6 py-3 rounded-md transition-all shadow-lg hover:-translate-y-0.5"
                >
                  <span>Get a Quote</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:077657126609"
                  onClick={() => trackCall("07765 712609", "service_hero")}
                  className="inline-flex items-center gap-2 bg-stone-800/60 hover:bg-stone-800 text-white font-semibold text-base px-6 py-3 rounded-md transition-all border border-stone-700"
                >
                  <Phone className="w-4 h-4 text-[#C9A84C]" />
                  <span>07765 712609</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Overview + Specs */}
        <section className="py-16 sm:py-20 bg-[#FAF8F5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Left: Long description */}
              <div className="lg:col-span-7">
                <span className="text-xs sm:text-sm uppercase tracking-widest text-[#B38D30] font-bold block mb-2">
                  OVERVIEW
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight mb-6">
                  {system.name} in Exeter & Devon
                </h2>
                <div className="space-y-4">
                  {system.longDescription.map((para, i) => (
                    <p
                      key={i}
                      className="text-stone-600 text-base leading-relaxed"
                    >
                      {para}
                    </p>
                  ))}
                </div>

                {/* Highlights */}
                <h3 className="text-lg font-bold text-stone-900 mt-8 mb-4">
                  Key Benefits
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {system.highlights.map((h) => (
                    <div
                      key={h}
                      className="flex items-start gap-2.5 p-3 bg-white border border-stone-200 rounded-lg"
                    >
                      <CheckCircle2 className="w-5 h-5 text-[#C9A84C] shrink-0 mt-0.5" />
                      <span className="text-stone-700 text-sm leading-relaxed">
                        {h}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Specs card */}
              <div className="lg:col-span-5">
                <div className="bg-white border border-stone-200 rounded-2xl shadow-lg p-6 sm:p-8 sticky top-32">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-[#B38D30]" />
                    Technical Specifications
                  </h4>
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {system.specs.map((spec) => (
                      <div
                        key={spec.label}
                        className="bg-stone-50 border border-stone-200 rounded-lg p-3"
                      >
                        <span className="block text-xs text-stone-500 mb-0.5">
                          {spec.label}
                        </span>
                        <span className="block text-sm font-bold text-stone-900">
                          {spec.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#B38D30]" />
                    Best For
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {system.bestFor.map((b) => (
                      <span
                        key={b}
                        className="inline-flex items-center gap-1.5 bg-[#C9A84C]/10 text-[#8c6b1f] text-xs font-semibold px-3 py-1.5 rounded-full border border-[#C9A84C]/20"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {b}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 pt-5 border-t border-stone-200 text-stone-500 mb-6">
                    <div className="flex items-center gap-1.5 text-xs">
                      <Clock className="w-4 h-4 text-[#B38D30]" />
                      <span>24–48h walk-on</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <Thermometer className="w-4 h-4 text-[#B38D30]" />
                      <span>UFH optimised</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <ShieldCheck className="w-4 h-4 text-[#B38D30]" />
                      <span>10-yr Cemfloor guarantee</span>
                    </div>
                  </div>

                  <Link
                    to="/#contact"
                    onClick={() => trackQuote("service_specs_card")}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold py-3 px-6 rounded-lg transition-all shadow-md"
                  >
                    <span>Get a Quote</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sector Applications */}
        <section className="py-16 sm:py-20 bg-white border-y border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs sm:text-sm uppercase tracking-widest text-[#B38D30] font-bold block mb-2">
                SECTOR APPLICATIONS
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
                Where {system.name}{" "}
                <span className="gold-gradient-text">Works Best</span>
              </h2>
              <p className="text-stone-500 text-sm">
                Typical projects where {system.tagline} is the ideal choice.
              </p>
            </div>
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 ${system.sectors.length === 3 ? "lg:grid-cols-3 max-w-4xl" : "lg:grid-cols-4"} mx-auto gap-5`}
            >
              {system.sectors.map((sector) => (
                <div
                  key={sector.title}
                  className="p-6 bg-stone-50 border border-stone-200 rounded-xl hover:border-[#C9A84C]/40 hover:shadow-md transition-all"
                >
                  <h3 className="text-base font-bold text-stone-900 mb-2">
                    {sector.title}
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    {sector.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 sm:py-20 bg-[#FAF8F5]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-xs sm:text-sm uppercase tracking-widest text-[#B38D30] font-bold block mb-2">
                FREQUENTLY ASKED QUESTIONS
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
                {system.name} Questions
              </h2>
            </div>
            <div className="space-y-4">
              {system.faqs.map((faq, i) => (
                <details
                  key={i}
                  className="group bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm"
                >
                  <summary className="flex items-center justify-between cursor-pointer p-5 text-left font-bold text-stone-900 text-base list-none">
                    {faq.question}
                    <ChevronRight className="w-5 h-5 text-[#B38D30] shrink-0 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-5 text-stone-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Screed Finder */}
        <ScreedFinder />

        {/* Other Systems */}
        <section className="py-16 sm:py-20 bg-white border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight mb-2">
                Explore Our Other Screeds
              </h2>
              <p className="text-stone-500 text-sm">
                See our full range of Cemfloor liquid screeds.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 max-w-4xl mx-auto gap-4">
              {otherSystems.map((s) => {
                const OtherIcon = s.icon;
                return (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="group flex flex-col items-center text-center p-5 bg-stone-50 border border-stone-200 rounded-xl hover:border-[#C9A84C]/50 hover:shadow-md transition-all"
                  >
                    <div className="p-3 rounded-xl bg-[#C9A84C]/10 text-[#B38D30] mb-3 group-hover:bg-[#C9A84C]/20 transition-colors">
                      <OtherIcon className="w-6 h-6" />
                    </div>
                    <span className="text-sm font-bold text-stone-800 group-hover:text-[#B38D30] transition-colors leading-tight">
                      {s.name}
                    </span>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/#what-we-do"
                className="inline-flex items-center gap-2 text-[#B38D30] hover:text-[#8c6b1f] font-bold text-sm transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                <span>Back to all screeds</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Contact */}
        <ContactAndMap />
      </main>
      <Footer />
    </div>
  );
};

export default ServiceDetail;
